import { g as getDocument, S as Swiper, E as EffectFade } from "@theme/effect-fade";
import { g as gsapWithCSS } from "@theme/theme";
function Autoplay({
  swiper,
  extendParams,
  on,
  emit,
  params
}) {
  swiper.autoplay = {
    running: false,
    paused: false,
    timeLeft: 0
  };
  extendParams({
    autoplay: {
      enabled: false,
      delay: 3e3,
      waitForTransition: true,
      disableOnInteraction: false,
      stopOnLastSlide: false,
      reverseDirection: false,
      pauseOnMouseEnter: false
    }
  });
  let timeout;
  let raf;
  let autoplayDelayTotal = params && params.autoplay ? params.autoplay.delay : 3e3;
  let autoplayDelayCurrent = params && params.autoplay ? params.autoplay.delay : 3e3;
  let autoplayTimeLeft;
  let autoplayStartTime = (/* @__PURE__ */ new Date()).getTime();
  let wasPaused;
  let isTouched;
  let pausedByTouch;
  let touchStartTimeout;
  let slideChanged;
  let pausedByInteraction;
  let pausedByPointerEnter;
  function onTransitionEnd(e) {
    if (!swiper || swiper.destroyed || !swiper.wrapperEl) return;
    if (e.target !== swiper.wrapperEl) return;
    swiper.wrapperEl.removeEventListener("transitionend", onTransitionEnd);
    if (pausedByPointerEnter || e.detail && e.detail.bySwiperTouchMove) {
      return;
    }
    resume();
  }
  const calcTimeLeft = () => {
    if (swiper.destroyed || !swiper.autoplay.running) return;
    if (swiper.autoplay.paused) {
      wasPaused = true;
    } else if (wasPaused) {
      autoplayDelayCurrent = autoplayTimeLeft;
      wasPaused = false;
    }
    const timeLeft = swiper.autoplay.paused ? autoplayTimeLeft : autoplayStartTime + autoplayDelayCurrent - (/* @__PURE__ */ new Date()).getTime();
    swiper.autoplay.timeLeft = timeLeft;
    emit("autoplayTimeLeft", timeLeft, timeLeft / autoplayDelayTotal);
    raf = requestAnimationFrame(() => {
      calcTimeLeft();
    });
  };
  const getSlideDelay = () => {
    let activeSlideEl;
    if (swiper.virtual && swiper.params.virtual.enabled) {
      activeSlideEl = swiper.slides.find((slideEl) => slideEl.classList.contains("swiper-slide-active"));
    } else {
      activeSlideEl = swiper.slides[swiper.activeIndex];
    }
    if (!activeSlideEl) return void 0;
    const currentSlideDelay = parseInt(activeSlideEl.getAttribute("data-swiper-autoplay"), 10);
    return currentSlideDelay;
  };
  const run = (delayForce) => {
    if (swiper.destroyed || !swiper.autoplay.running) return;
    cancelAnimationFrame(raf);
    calcTimeLeft();
    let delay = typeof delayForce === "undefined" ? swiper.params.autoplay.delay : delayForce;
    autoplayDelayTotal = swiper.params.autoplay.delay;
    autoplayDelayCurrent = swiper.params.autoplay.delay;
    const currentSlideDelay = getSlideDelay();
    if (!Number.isNaN(currentSlideDelay) && currentSlideDelay > 0 && typeof delayForce === "undefined") {
      delay = currentSlideDelay;
      autoplayDelayTotal = currentSlideDelay;
      autoplayDelayCurrent = currentSlideDelay;
    }
    autoplayTimeLeft = delay;
    const speed = swiper.params.speed;
    const proceed = () => {
      if (!swiper || swiper.destroyed) return;
      if (swiper.params.autoplay.reverseDirection) {
        if (!swiper.isBeginning || swiper.params.loop || swiper.params.rewind) {
          swiper.slidePrev(speed, true, true);
          emit("autoplay");
        } else if (!swiper.params.autoplay.stopOnLastSlide) {
          swiper.slideTo(swiper.slides.length - 1, speed, true, true);
          emit("autoplay");
        }
      } else {
        if (!swiper.isEnd || swiper.params.loop || swiper.params.rewind) {
          swiper.slideNext(speed, true, true);
          emit("autoplay");
        } else if (!swiper.params.autoplay.stopOnLastSlide) {
          swiper.slideTo(0, speed, true, true);
          emit("autoplay");
        }
      }
      if (swiper.params.cssMode) {
        autoplayStartTime = (/* @__PURE__ */ new Date()).getTime();
        requestAnimationFrame(() => {
          run();
        });
      }
    };
    if (delay > 0) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        proceed();
      }, delay);
    } else {
      requestAnimationFrame(() => {
        proceed();
      });
    }
    return delay;
  };
  const start = () => {
    autoplayStartTime = (/* @__PURE__ */ new Date()).getTime();
    swiper.autoplay.running = true;
    run();
    emit("autoplayStart");
  };
  const stop = () => {
    swiper.autoplay.running = false;
    clearTimeout(timeout);
    cancelAnimationFrame(raf);
    emit("autoplayStop");
  };
  const pause = (internal, reset) => {
    if (swiper.destroyed || !swiper.autoplay.running) return;
    clearTimeout(timeout);
    if (!internal) {
      pausedByInteraction = true;
    }
    const proceed = () => {
      emit("autoplayPause");
      if (swiper.params.autoplay.waitForTransition) {
        swiper.wrapperEl.addEventListener("transitionend", onTransitionEnd);
      } else {
        resume();
      }
    };
    swiper.autoplay.paused = true;
    if (reset) {
      if (slideChanged) {
        autoplayTimeLeft = swiper.params.autoplay.delay;
      }
      slideChanged = false;
      proceed();
      return;
    }
    const delay = autoplayTimeLeft || swiper.params.autoplay.delay;
    autoplayTimeLeft = delay - ((/* @__PURE__ */ new Date()).getTime() - autoplayStartTime);
    if (swiper.isEnd && autoplayTimeLeft < 0 && !swiper.params.loop) return;
    if (autoplayTimeLeft < 0) autoplayTimeLeft = 0;
    proceed();
  };
  const resume = () => {
    if (swiper.isEnd && autoplayTimeLeft < 0 && !swiper.params.loop || swiper.destroyed || !swiper.autoplay.running) return;
    autoplayStartTime = (/* @__PURE__ */ new Date()).getTime();
    if (pausedByInteraction) {
      pausedByInteraction = false;
      run(autoplayTimeLeft);
    } else {
      run();
    }
    swiper.autoplay.paused = false;
    emit("autoplayResume");
  };
  const onVisibilityChange = () => {
    if (swiper.destroyed || !swiper.autoplay.running) return;
    const document = getDocument();
    if (document.visibilityState === "hidden") {
      pausedByInteraction = true;
      pause(true);
    }
    if (document.visibilityState === "visible") {
      resume();
    }
  };
  const onPointerEnter = (e) => {
    if (e.pointerType !== "mouse") return;
    pausedByInteraction = true;
    pausedByPointerEnter = true;
    if (swiper.animating || swiper.autoplay.paused) return;
    pause(true);
  };
  const onPointerLeave = (e) => {
    if (e.pointerType !== "mouse") return;
    pausedByPointerEnter = false;
    if (swiper.autoplay.paused) {
      resume();
    }
  };
  const attachMouseEvents = () => {
    if (swiper.params.autoplay.pauseOnMouseEnter) {
      swiper.el.addEventListener("pointerenter", onPointerEnter);
      swiper.el.addEventListener("pointerleave", onPointerLeave);
    }
  };
  const detachMouseEvents = () => {
    if (swiper.el && typeof swiper.el !== "string") {
      swiper.el.removeEventListener("pointerenter", onPointerEnter);
      swiper.el.removeEventListener("pointerleave", onPointerLeave);
    }
  };
  const attachDocumentEvents = () => {
    const document = getDocument();
    document.addEventListener("visibilitychange", onVisibilityChange);
  };
  const detachDocumentEvents = () => {
    const document = getDocument();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
  on("init", () => {
    if (swiper.params.autoplay.enabled) {
      attachMouseEvents();
      attachDocumentEvents();
      start();
    }
  });
  on("destroy", () => {
    detachMouseEvents();
    detachDocumentEvents();
    if (swiper.autoplay.running) {
      stop();
    }
  });
  on("_freeModeStaticRelease", () => {
    if (pausedByTouch || pausedByInteraction) {
      resume();
    }
  });
  on("_freeModeNoMomentumRelease", () => {
    if (!swiper.params.autoplay.disableOnInteraction) {
      pause(true, true);
    } else {
      stop();
    }
  });
  on("beforeTransitionStart", (_s, speed, internal) => {
    if (swiper.destroyed || !swiper.autoplay.running) return;
    if (internal || !swiper.params.autoplay.disableOnInteraction) {
      pause(true, true);
    } else {
      stop();
    }
  });
  on("sliderFirstMove", () => {
    if (swiper.destroyed || !swiper.autoplay.running) return;
    if (swiper.params.autoplay.disableOnInteraction) {
      stop();
      return;
    }
    isTouched = true;
    pausedByTouch = false;
    pausedByInteraction = false;
    touchStartTimeout = setTimeout(() => {
      pausedByInteraction = true;
      pausedByTouch = true;
      pause(true);
    }, 200);
  });
  on("touchEnd", () => {
    if (swiper.destroyed || !swiper.autoplay.running || !isTouched) return;
    clearTimeout(touchStartTimeout);
    clearTimeout(timeout);
    if (swiper.params.autoplay.disableOnInteraction) {
      pausedByTouch = false;
      isTouched = false;
      return;
    }
    if (pausedByTouch && swiper.params.cssMode) resume();
    pausedByTouch = false;
    isTouched = false;
  });
  on("slideChange", () => {
    if (swiper.destroyed || !swiper.autoplay.running) return;
    slideChanged = true;
  });
  Object.assign(swiper.autoplay, {
    start,
    stop,
    pause,
    resume
  });
}
class HeroComponent extends window.HTMLElement {
  constructor() {
    super();
    this.DOM = {
      slider: this.querySelector(".js-hero-image-slider"),
      linksSlider: this.querySelector(".js-hero-link-slider"),
      links: [...this.querySelectorAll(".js-hero-link")]
    };
    this.mm = gsapWithCSS.matchMedia();
  }
  connectedCallback() {
    this.initSlider();
    this.initLinks();
  }
  initLinks() {
    if (!this.DOM.links.length) return;
    this.mm.add("(min-width: 768px)", () => {
      this.playLinksSequence();
    });
    this.mm.add("(max-width: 767.98px)", () => {
      return this.initLinksSlider();
    });
    this.mm.add("(hover: hover)", (context) => {
      context.add("spin", (e) => {
        const link = e.currentTarget;
        const lottie = link.querySelector(".js-hero-link-lottie");
        if (!lottie || gsapWithCSS.isTweening(lottie)) return;
        gsapWithCSS.to(lottie, { rotation: "+=360", duration: 0.8, ease: "power2.out" });
      });
      this.DOM.links.forEach((link) => {
        link.addEventListener("mouseenter", context.spin);
      });
      return () => {
        this.DOM.links.forEach((link) => {
          link.removeEventListener("mouseenter", context.spin);
        });
      };
    });
  }
  async playLinksSequence() {
    await window.customElements.whenDefined("c-lottie");
    const links = await Promise.all(
      this.DOM.links.map(async (el) => {
        const lottie = el.querySelector(".js-hero-link-lottie c-lottie");
        const text = el.querySelector(".js-hero-link-text");
        return {
          el,
          lottie,
          text,
          duration: lottie ? await lottie.getDuration() : 0
        };
      })
    );
    const textFade = 1;
    const groupOverlap = 0.5;
    let startAt = 0;
    const tl = gsapWithCSS.timeline({ delay: 0.5 });
    links.forEach(({ el, lottie, text, duration }) => {
      tl.set(el, { autoAlpha: 1 }, startAt);
      if (lottie) {
        tl.call(() => lottie.play(), null, startAt);
      }
      const textStart = startAt + duration;
      if (text) {
        tl.to(text, { autoAlpha: 1, duration: textFade, ease: "power2.out" }, textStart);
      }
      startAt = textStart - groupOverlap;
    });
    this.linksTimeline = tl;
  }
  initLinksSlider() {
    if (!this.DOM.linksSlider) return;
    const playActiveSlide = (swiper2) => {
      const slide = swiper2.slides[swiper2.activeIndex];
      if (!slide) return;
      const lottie = slide.querySelector(".js-hero-link-lottie c-lottie");
      const text = slide.querySelector(".js-hero-link-text");
      if (text) gsapWithCSS.fromTo(text, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1, ease: "power2.out" });
      if (lottie) lottie.play();
    };
    const swiper = new Swiper(this.DOM.linksSlider, {
      rewind: true,
      slidesPerView: 1,
      speed: 1e3,
      effect: "fade",
      fadeEffect: { crossFade: true },
      autoplay: {
        delay: 3e3,
        disableOnInteraction: false
      },
      modules: [EffectFade, Autoplay],
      on: {
        init: async (swiper2) => {
          await window.customElements.whenDefined("c-lottie");
          playActiveSlide(swiper2);
        },
        slideChange: playActiveSlide
      }
    });
    this.linksSwiper = swiper;
    return () => {
      swiper.destroy(true, true);
      this.linksSwiper = null;
    };
  }
  initSlider() {
    if (!this.DOM.slider) return;
    new Swiper(this.DOM.slider, {
      loop: true,
      slidesPerView: 1,
      speed: 1e3,
      effect: "fade",
      fadeEffect: {
        crossFade: false
      },
      autoplay: {
        delay: 3e3,
        disableOnInteraction: false
      },
      modules: [EffectFade, Autoplay],
      on: {
        init: (swiper) => {
          swiper.autoplay.stop();
          gsapWithCSS.delayedCall(1.5, () => {
            swiper.autoplay.start();
          });
        }
      }
    });
  }
}
window.customElements.define("c-hero", HeroComponent);
